import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import api from '../../utils/api';
import { api_url } from '../../config';
import PlanCard from './PlanCard';
import ErrorModal from '../ErrorModal';
import { FaRegCalendarAlt } from 'react-icons/fa';

const PlanComponent = ({ planData, promoRef, isAuthenticated = false }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { id, token } = useSelector((state) => state.auth);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [errorModal, setErrorModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getClientSecret = async (plan) => {
    if (!isAuthenticated) return;
    
    try {
      setLoadingPlanId(plan.id);
      const data = await api.get(`${api_url}/v1/user/${id}/check-payment-method`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const client_secret = data?.data?.client_secret;
      const message = data?.data?.message;
      const user_name = data?.data?.name;
      const user_email = data?.data?.email;
      const pm_type = data?.data?.pm_type;
      const pm_last_four = data?.data?.pm_last_four;

      navigate('/subscribe', {
        state: {
          client_secret,
          message,
          user_name,
          user_email,
          pm_type,
          pm_last_four,
          plan,
        },
      });
    } catch (error) {
      console.error("Error getting client secret:", error);
      setErrorModal(true);
    } finally {
      setLoadingPlanId(null);
    }
  };

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
  };

  const activePlans = planData?.filter(item => item.status === 'active') || [];

  // Show a message when no active plans are available
  if (activePlans.length === 0) {
    return (
      <div ref={promoRef} className="flex flex-col items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8 text-center">
        <FaRegCalendarAlt className="text-blue-500 text-5xl mb-4" />
        <h3 className="text-xl font-bold text-gray-800 mb-2">Coming Soon!</h3>
        <p className="text-gray-600 mb-4 max-w-md">
          We're currently working on exciting subscription plans for this product.
          Check back soon or subscribe to our newsletter to be notified when they become available.
        </p>
        <button className="bg-blue-600 text-white py-2 px-6 rounded-full hover:bg-blue-700 transition-colors duration-300">
          Get Notified
        </button>
      </div>
    );
  }

  return (
    <div ref={promoRef}>
      {isMobile ? (
        <Slider {...sliderSettings}>
          {activePlans.map((item) => (
            <div key={item.id} className="px-1">
              <PlanCard
                id={item.id}
                name={item.name}
                price={item.price}
                billable={item.billable}
                description={item.description}
                features={item.features}
                stripe_price={item.stripe_price}
                discounted_amount={item.discounted_amount}
                onBuy={() => getClientSecret(item)}
                loader={loadingPlanId === item.id}
                isAuthenticated={isAuthenticated}
              />
            </div>
          ))}
        </Slider>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {activePlans.map((item) => (
            <div key={item.id}>
              <PlanCard
                id={item.id}
                name={item.name}
                price={item.price}
                billable={item.billable}
                description={item.description}
                features={item.features}
                stripe_price={item.stripe_price}
                discounted_amount={item.discounted_amount}
                onBuy={() => getClientSecret(item)}
                loader={loadingPlanId === item.id}
                isAuthenticated={isAuthenticated}
              />
            </div>
          ))}
        </div>
      )}

      {errorModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-90">
          <div className="bg-white rounded-md min-h-max shadow-lg p-3">
            <ErrorModal toggle={() => setErrorModal(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanComponent; 